import {
    Paper,
    Box,
    TextField,
    Button,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent 
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { Actions, Thunks } from '../../actions';
import { useAuthHelper } from '../../components/AuthContextProvider';
import { ContentContainer } from '../../components/ContentContainer';
import { HeaderInfo } from '../../components/HeaderInfo/HeaderInfo';
import { NavigationPromptModal } from '../../components/NavigationPromptModal/NavigationPromptModal';
import { DECIMAL_NUMBER, IP_ADDRESS, FX30_ETHERNET, FX30S_SERIAL } from '../../constants';
import { useToasts } from '../../data/DataProviders/ToastProvider';
import { pushAppRoute } from '../../data/domain/route-manager';
import { useCurrentAppRoute } from '../../hooks/app-route-hooks';
import { isRecordFound, useSelectedOrganizationData } from '../../hooks/data-hooks';
import { useLanguageLocale } from '../../hooks/language-locale-hooks';
import { Selectors } from '../../selectors';
import { SierraDeviceData, GatewayDeviceConfig, ConfigTree } from '../../types/device';
import deviceStyles from './DeviceStyle';
import locationStyles from '../location-management/LocationStyle';
import Chip from '@mui/material/Chip';
import { DeviceAutoComplete } from '../../components/DeviceAutoComplete/DeviceAutoComplete';
import { baudData, bits, term, standard, parity, stop } from '../../constants/device-data';

export const EditConnectedDevice: React.FC<{
    sierraDeviceDetails: SierraDeviceData | null;
    updateGatewayDeviceData: Function;
    deviceTypeList: any[];
}> = (props) => {
    const history = useHistory();
    const currentAppRoute = useCurrentAppRoute();
    const { t } = useLanguageLocale();
    const classes = locationStyles();
    const deviceClasses = deviceStyles();
    const dispatch = useDispatch();
    const authHelper = useAuthHelper();
    const { addToast } = useToasts();
    const selectedOrganization = useSelectedOrganizationData();
    const gatewayDeviceState = useSelector(Selectors.SierraDevice.deviceRegistration);
    const [unmodifiedDeviceInfo, setUnmodifiedDeviceInfo] = useState<SierraDeviceData | null>(null);
    const [modifiedDeviceInfo, setModifiedDeviceInfo] = useState<SierraDeviceData | null>(null);
    const [ipAddressError, setIPAddressError] = React.useState(false);
    const [isDirty, setIsDirty] = useState(false);
    // const [netmask, setNetMask] = useState('');
    const [configTreeJSON, setConfigTreeJSON] = useState<ConfigTree | null>(null);
    const [gatewayDeviceData, setGatewayDeviceData] = useState<GatewayDeviceConfig | null>(null);
    const [initialGatewayDeviceData, setInitialGatewayDeviceData] = useState<GatewayDeviceConfig | null>(null);

    const navigateToConnectedDeviceProfile = (): void => {
        if (currentAppRoute.type === 'EditConnectedDevice')
            pushAppRoute(history, {
                type: 'DeviceProfile',
                deviceId: currentAppRoute.deviceId,
                locationId: currentAppRoute.locationId,
                orgId: currentAppRoute.orgId,
                siteId: currentAppRoute.siteId,
            });
    };

    useEffect(() => {
        if (!gatewayDeviceState.gatewayLoading && gatewayDeviceState.gatewaySuccess) {
            addToast('Connected Device updated successfully.');

            dispatch(Actions.SierraDevices.sierraGatewayDeviceUpdateReset());
            navigateToConnectedDeviceProfile();
        }
    }, [gatewayDeviceState.gatewayLoading, gatewayDeviceState.gatewaySuccess]); //eslint-disable-line react-hooks/exhaustive-deps

    const isDisable = (): boolean => {
        if (!gatewayDeviceState.gatewayLoading)
            if (modifiedDeviceInfo?.model.toLowerCase() === FX30S_SERIAL) {
                return  !(gatewayDeviceData && gatewayDeviceData.uart1.flow.trim() && isDirty);
                
            } else {
                return  !(
                    modifiedDeviceInfo &&
                    modifiedDeviceInfo.ipAddress.trim() &&
                    gatewayDeviceData?.ethernet.netmask &&
                    isDirty
                );
               
            }
        return true;
    };
    useEffect(() => {
        dispatch(Actions.SierraDevices.sierraGatewayDeviceRegistrationUnmounted());
    }, []); //eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (props.sierraDeviceDetails) {
            setUnmodifiedDeviceInfo(JSON.parse(JSON.stringify(props.sierraDeviceDetails)));
            setModifiedDeviceInfo(props.sierraDeviceDetails);
            const value: any = props.sierraDeviceDetails.configTreeJson
            setConfigTreeJSON(value);
            if (value) {
                const data: GatewayDeviceConfig = value.gatewayDevice;
                setInitialGatewayDeviceData(JSON.parse(JSON.stringify(data)));
                setGatewayDeviceData(data);
                
            }
        }
    }, [props.sierraDeviceDetails]);


    useEffect(() => {
        const isDataModified = JSON.stringify(unmodifiedDeviceInfo) !== JSON.stringify(modifiedDeviceInfo);
       const isgatewayDataModified = JSON.stringify(initialGatewayDeviceData) !== JSON.stringify(gatewayDeviceData);
        setIsDirty(isDataModified || isgatewayDataModified);
    }, [unmodifiedDeviceInfo, modifiedDeviceInfo,initialGatewayDeviceData, gatewayDeviceData]); //eslint-disable-line react-hooks/exhaustive-deps

    const onChangeIpAddress = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if ((e.target.value === '' || DECIMAL_NUMBER.test(e.target.value)) && modifiedDeviceInfo) {
            setIPAddressError(false);
            const deviceIpAddressObj: SierraDeviceData = { ...modifiedDeviceInfo };
            deviceIpAddressObj.ipAddress = e.target.value;
            setModifiedDeviceInfo(deviceIpAddressObj);
        }
    };

    const onChangeNetMask = (e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>): void => {
        if (
            (e.target.value === '' || DECIMAL_NUMBER.test(e.target.value)) &&
            modifiedDeviceInfo &&
            gatewayDeviceData &&
            configTreeJSON
        ) {
            const gatewaynetMaskObj: GatewayDeviceConfig = { ...gatewayDeviceData };
            gatewaynetMaskObj.ethernet.netmask = Number(e.target.value);
            setGatewayDeviceData(gatewaynetMaskObj);
            const deviceIpAddressObj: SierraDeviceData = { ...modifiedDeviceInfo };
            configTreeJSON.gatewayDevice = gatewaynetMaskObj;
            deviceIpAddressObj.configTreeJson = configTreeJSON;
            setModifiedDeviceInfo(deviceIpAddressObj);
        }
    };

    const handleGatewayDataChange = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent,
        type: string
    ): void => {
        if (gatewayDeviceData) {
            const updatedgatewayObj: any = { ...gatewayDeviceData };
            updatedgatewayObj.uart1[`${type}`] = (type === 'bits' || type=== 'stop') ? Number(event.target.value):event.target.value;
            setGatewayDeviceData(updatedgatewayObj);
        }
    };

    const onSubmit = (): void => {
        setIsDirty(false);

        if (configTreeJSON && gatewayDeviceData && modifiedDeviceInfo && isRecordFound(selectedOrganization)) {
            if (
                (modifiedDeviceInfo.model.toLowerCase() === FX30_ETHERNET &&
                    IP_ADDRESS.test(modifiedDeviceInfo.ipAddress)) ||
                modifiedDeviceInfo.model.toLowerCase() === FX30S_SERIAL
            ) {
                const updateddeviceObj: SierraDeviceData = { ...modifiedDeviceInfo };
                configTreeJSON.gatewayDevice = gatewayDeviceData;
                updateddeviceObj.configTreeJson = configTreeJSON;
                setModifiedDeviceInfo(updateddeviceObj);
                setIPAddressError(false);
                dispatch(
                    Thunks.SierraDevice.UpdateGatewayDevice(
                        modifiedDeviceInfo.pxWhiteGatewayId || '',
                        modifiedDeviceInfo.octaveGatewayId || '',
                        modifiedDeviceInfo.siteId,
                        modifiedDeviceInfo.childDeviceTypeList,
                        modifiedDeviceInfo.ipAddress,
                        gatewayDeviceData.uart1,
                        gatewayDeviceData.ethernet.netmask,
                        selectedOrganization.value.name,
                        authHelper,
                        addToast
                    )
                );
                props.updateGatewayDeviceData(updateddeviceObj);
            } else setIPAddressError(true);
        }
    };

    const selectedDevices = (): void => void 0;

    return <>
        <ContentContainer>
            <Paper style={{ padding: 48 }}>
                <HeaderInfo title="Edit Connected Device Information" entity="when editing a Connected Device" />
                {props.sierraDeviceDetails && modifiedDeviceInfo && gatewayDeviceData && (
                    <Box display="flex" flexDirection="column" alignItems="left">
                        <Box className={classes.paddingTextbox}>
                            <DeviceAutoComplete
                                selectedDevices={selectedDevices}
                                isBlueprintExist={!gatewayDeviceState.hasData}
                                disabled={true}
                            />
                        </Box>
                        <Box
                            data-testid="device-chip-wrapper"
                            flexDirection="row"
                            alignItems="center"
                            display="flex"
                            flexWrap="wrap"
                            width="100%"
                        >
                            {props.deviceTypeList.map((deviceData) => (
                                <Chip
                                    data-testid="device-chip"
                                    key={deviceData.key}
                                    label={deviceData.value.displayName}
                                    style={{ margin: '3px 8px' }}
                                    disabled />
                            ))}
                        </Box>
                        <Box className={classes.paddingTextbox}>
                            <TextField
                                inputProps={{ 'data-testid': 'device-name' }}
                                fullWidth
                                required
                                disabled
                                variant="filled"
                                id="ConnectedDevice"
                                label="Connected Device Name"
                                value={props.sierraDeviceDetails.name}
                            />
                        </Box>
                        <Box className={classes.paddingTextbox}>
                            <TextField
                                inputProps={{ 'data-testid': 'serial-number' }}
                                fullWidth
                                disabled
                                required
                                variant="filled"
                                id="serial-number"
                                label="Serial Number"
                                value={props.sierraDeviceDetails.serialNumber}
                            />
                        </Box>
                        <Box className={classes.paddingTextbox}>
                            <TextField
                                inputProps={{ 'data-testid': 'imei' }}
                                fullWidth
                                required
                                disabled
                                variant="filled"
                                id="imei"
                                label="IMEI"
                                value={props.sierraDeviceDetails.imei}
                            />
                        </Box>
                        {modifiedDeviceInfo.model.toLowerCase() === FX30_ETHERNET && gatewayDeviceData && (
                            <>
                                <Box className={classes.paddingTextbox}>
                                    <TextField
                                        inputProps={{ 'data-testid': 'ip-address' }}
                                        fullWidth
                                        required
                                        variant="filled"
                                        id="ipaddress"
                                        error={ipAddressError}
                                        helperText={ipAddressError ? 'Invalid IP Address' : ''}
                                        onChange={(e): void => {
                                            onChangeIpAddress(e);
                                        }}
                                        label="IP Address"
                                        value={modifiedDeviceInfo.ipAddress}
                                    />
                                </Box>
                                <Box className={classes.paddingTextbox}>
                                    <TextField
                                        inputProps={{ 'data-testid': 'net-mask' }}
                                        fullWidth
                                        required
                                        variant="filled"
                                        id="netmask"
                                        onChange={(e): void => {
                                            onChangeNetMask(e);
                                        }}
                                        label="Netmask"
                                        value={gatewayDeviceData.ethernet.netmask}
                                    />
                                </Box>
                            </>
                        )}
                        {modifiedDeviceInfo.model.toLowerCase() === FX30S_SERIAL && (
                            <>
                                <Box className={classes.paddingTextbox}>
                                    <FormControl variant="filled" required className={`${deviceClasses.fullWidth}`}>
                                        <InputLabel id="baudRateData" data-testid="baudrate-label-dd">
                                            Baud Rate
                                        </InputLabel>
                                        <Select
                                            label="BaudRate"
                                            onChange={(e): void => {
                                                handleGatewayDataChange(e, 'baud');
                                            }}
                                            variant="filled"
                                            defaultValue="0"
                                            value={gatewayDeviceData.uart1.baud}
                                            required
                                            inputProps={{ 'data-testid': 'baudrate-dd' }}
                                        >
                                            {baudData.map((baud: { key: string; value: string }) => (
                                                <MenuItem data-testid="baud-menu" key={baud.key} value={baud.value}>
                                                    {baud.key}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box
                                    className={classes.paddingTextbox}
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <FormControl variant="filled" required className={`${deviceClasses.halfWidth}`}>
                                        <InputLabel id="databitsData" data-testid="databits-label-dd">
                                            Data Bits
                                        </InputLabel>
                                        <Select
                                            label="DataBits"
                                            onChange={(e): void => {
                                                handleGatewayDataChange(e, 'bits');
                                            }}
                                            variant="filled"
                                            defaultValue="0"
                                            value={gatewayDeviceData.uart1.bits.toString()}
                                            required
                                            inputProps={{ 'data-testid': 'databits-dd' }}
                                        >
                                            {bits.map((bit: { key: number; value: string }) => (
                                                <MenuItem data-testid="bits-menu" key={bit.key} value={bit.value}>
                                                    {bit.key}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <TextField
                                        inputProps={{ 'data-testid': 'flow' }}
                                        required
                                        variant="filled"
                                        id="flow"
                                        onChange={(e): void => {
                                            handleGatewayDataChange(e, 'flow');
                                        }}
                                        label="Flow"
                                        value={'N'}
                                        disabled
                                        className={`${deviceClasses.halfWidth}`}
                                    />
                                </Box>

                                <Box
                                    className={classes.paddingTextbox}
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <FormControl variant="filled" required className={`${deviceClasses.halfWidth}`}>
                                        <InputLabel id="parityData" data-testid="parity-label-dd">
                                            Parity
                                        </InputLabel>
                                        <Select
                                            label="Parity"
                                            onChange={(e): void => {
                                                handleGatewayDataChange(e, 'pair');
                                            }}
                                            variant="filled"
                                            defaultValue="0"
                                            value={gatewayDeviceData.uart1.pair}
                                            required
                                            inputProps={{ 'data-testid': 'parity-dd' }}
                                        >
                                            {parity.map((par: { key: string; value: string }) => (
                                                <MenuItem data-testid="parity-menu" key={par.key} value={par.key}>
                                                    {par.value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl variant="filled" required className={`${deviceClasses.halfWidth}`}>
                                        <InputLabel id="stopBitsData" data-testid="stopBits-label-dd">
                                            Stop Bits
                                        </InputLabel>
                                        <Select
                                            label="StopBits"
                                            onChange={(e): void => {
                                                handleGatewayDataChange(e, 'stop');
                                            }}
                                            variant="filled"
                                            defaultValue="0"
                                            value={gatewayDeviceData.uart1.stop.toString()}
                                            required
                                            inputProps={{ 'data-testid': 'stopBits-dd' }}
                                        >
                                            {stop.map((stp: { key: number; value: string }) => (
                                                <MenuItem data-testid="baud-menu" key={stp.key} value={stp.value}>
                                                    {stp.key}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                                <Box
                                    className={classes.paddingTextbox}
                                    display="flex"
                                    justifyContent="space-between"
                                >
                                    <FormControl variant="filled" required className={`${deviceClasses.halfWidth}`}>
                                        <InputLabel id="standardData" data-testid="standard-label-dd">
                                            Standard
                                        </InputLabel>
                                        <Select
                                            label="Standard"
                                            onChange={(e): void => {
                                                handleGatewayDataChange(e, 'std');
                                            }}
                                            variant="filled"
                                            defaultValue="0"
                                            value={gatewayDeviceData.uart1.std}
                                            required
                                            inputProps={{ 'data-testid': 'standard-dd' }}
                                        >
                                            {standard.map((std: { key: string; value: string }) => (
                                                <MenuItem data-testid="std-menu" key={std.key} value={std.key}>
                                                    {std.value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <FormControl variant="filled" required className={`${deviceClasses.halfWidth}`}>
                                        <InputLabel id="termData" data-testid="term-label-dd">
                                            Terminating Resistor Support
                                        </InputLabel>
                                        <Select
                                            label="Terminating Resistor Support"
                                            onChange={(e): void => {
                                                handleGatewayDataChange(e, 'term');
                                            }}
                                            variant="filled"
                                            defaultValue="0"
                                            value={gatewayDeviceData.uart1.term}
                                            required
                                            inputProps={{ 'data-testid': 'term-dd' }}
                                        >
                                            {term.map((trm: { key: string; value: string }) => (
                                                <MenuItem data-testid="term-menu" key={trm.key} value={trm.value}>
                                                    {trm.key}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Box>
                            </>
                        )}
                        <Box className={classes.iconRightEnd}>
                            <Button
                                style={{ marginRight: 16 }}
                                variant="outlined"
                                color="primary"
                                data-testid="cancel-device"
                                onClick={navigateToConnectedDeviceProfile}
                            >
                                {t('ACTIONS.CANCEL')}
                            </Button>
                            <Button
                                startIcon={
                                    gatewayDeviceState.gatewayLoading ? (
                                        <CircularProgress size="1rem" style={{ color: 'white' }} />
                                    ) : (
                                        <></>
                                    )
                                }
                                variant="contained"
                                disabled={isDisable()}
                                color="primary"
                                data-testid="edit-device-button"
                                onClick={onSubmit}
                            >
                                {t('ACTIONS.SUBMITCHANGES')}
                            </Button>
                        </Box>
                    </Box>
                )}
            </Paper>
        </ContentContainer>
        <NavigationPromptModal isDirty={isDirty} />
    </>;
};
